import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

import { SortingLotStockOutForm } from './new'
import type { LotOption } from '@/components/lots/lot-items-field'
import type { CreateOutNoteRequest } from '@/lib/api/sorting-lot-api'
import { sortingLotApi } from '@/lib/api/sorting-lot-api'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import {
    useSortingLots,
    useSortingOutNote,
    useUpdateSortingOutNote,
} from '@/hooks/use-sorting-lot-query'
import { useDebounced } from '@/hooks/use-debounced'
import {
    sortingLotOption,
    sortingNoteItemOption,
} from '@/components/lots/lot-option-builders'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sorting-warehouse/stock-out-notes/$id_/edit',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data, isLoading } = useSortingOutNote(id)
    const note = data?.data
    const update = useUpdateSortingOutNote()

    const [lotSearch, setLotSearch] = useState('')
    const debouncedSearch = useDebounced(lotSearch)
    const { data: lotsData } = useSortingLots({
        per_page: 50,
        only_available: '1',
        search: debouncedSearch || undefined,
    })
    const lotOptions = useMemo<LotOption[]>(
        () => (lotsData?.data ?? []).map(sortingLotOption),
        [lotsData],
    )
    const noteLotSeeds = useMemo<LotOption[]>(
        () => (note?.items ?? []).map(sortingNoteItemOption),
        [note],
    )
    const [scannedLots, setScannedLots] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await sortingLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, sortingLotOption(l))
                return next
            })
        } catch {
            // ignore
        }
    }
    const mergedLotOptions = useMemo<LotOption[]>(() => {
        const map = new Map<string, LotOption>()
        for (const opt of noteLotSeeds) map.set(opt.value, opt)
        for (const opt of lotOptions) map.set(opt.value, opt)
        for (const opt of scannedLots.values()) map.set(opt.value, opt)
        return Array.from(map.values())
    }, [noteLotSeeds, lotOptions, scannedLots])

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable =
        note.status === 'pending' && isCreator && !note.stocktake_session_id

    if (!editable) {
        return (
            <div className="space-y-4 p-6">
                <Button asChild variant="ghost" className="-ml-2">
                    <Link
                        to="/sorting-warehouse/stock-out-notes/$id"
                        params={{ id }}
                    >
                        <ArrowLeft className="size-4 mr-1" />
                        {m.sl_lot_back()}
                    </Link>
                </Button>
                <p className="text-muted-foreground">
                    {m.pl_pnk_edit_not_allowed()}
                </p>
            </div>
        )
    }

    const initialValues = {
        reason: note.reason,
        notes: note.notes ?? '',
        items: (note.items ?? []).map((it) => ({
            kind: 'existing' as const,
            sorting_lot_id: it.sorting_lot_id,
            quantity: it.quantity,
        })),
    }

    const backToDetail = () =>
        navigate({
            to: '/sorting-warehouse/stock-out-notes/$id',
            params: { id },
        })

    const handleSubmit = async (value: CreateOutNoteRequest) => {
        try {
            await update.mutateAsync({
                id,
                req: {
                    reason: value.reason,
                    notes: value.notes,
                    items: value.items,
                },
            })
            void backToDetail()
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <SortingLotStockOutForm
            mode="edit"
            title={m.sl_pxk_edit_title()}
            submitLabel={m.pl_pnk_edit_submit()}
            initialValues={initialValues}
            lotOptions={mergedLotOptions}
            scannedLots={Array.from(scannedLots.values())}
            onLotSearchChange={setLotSearch}
            onLotScanned={(lotId) => void fetchAndCacheLot(lotId)}
            isPending={update.isPending}
            onCancel={() => void backToDetail()}
            onSubmit={(value) => void handleSubmit(value)}
        />
    )
}
